import React, { useMemo, useState } from "react";
import {
  Modal,
  Box,
  Tabs,
  Tab,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

/**
 * CreatedByPickerModal
 *
 * A tabbed picker (Users / Organizations) with a search bar used to set the
 * "Created By" value on a QuickTale. Selecting a row returns the chosen item to
 * the parent, which stores only the id (users.id or organizations.id) — no type
 * flag is persisted. A `_sourceType` field is attached to the returned object
 * purely for local display purposes.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - usersData: array of user docs ({ id, firstName, lastName, userName, ... })
 *  - organizationsData: array of org docs ({ id, name, ... })
 *  - onSelect: (item) => void  // item includes _sourceType: 'user' | 'organization'
 */
const CreatedByPickerModal = ({
  open,
  onClose,
  usersData = [],
  organizationsData = [],
  onSelect,
}) => {
  const [tab, setTab] = useState(0); // 0 = Users, 1 = Organizations
  const [search, setSearch] = useState("");

  const getUserLabel = (user) => {
    const displayName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return user.userName ? `${displayName} /@${user.userName}` : displayName;
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = (usersData || []).filter((u) => u && u.id);
    if (!term) return list;
    return list.filter((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const uname = (u.userName || "").toLowerCase();
      return name.includes(term) || uname.includes(term);
    });
  }, [usersData, search]);

  const filteredOrgs = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = (organizationsData || []).filter((o) => o && o.id);
    if (!term) return list;
    return list.filter((o) => (o.name || "").toLowerCase().includes(term));
  }, [organizationsData, search]);

  const handleTabChange = (_e, newValue) => {
    setTab(newValue);
    setSearch("");
  };

  const handleSelectUser = (user) => {
    onSelect({ ...user, _sourceType: "user" });
    handleClose();
  };

  const handleSelectOrg = (org) => {
    onSelect({ ...org, _sourceType: "organization" });
    handleClose();
  };

  const handleClose = () => {
    setSearch("");
    setTab(0);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyles}>
        <Box sx={headerStyles}>
          <Typography variant="h6" sx={{ fontFamily: "Avenir Book, sans-serif", fontWeight: 700 }}>
            Set Created By
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ borderBottom: "1px solid #eee" }}
          TabIndicatorProps={{ style: { backgroundColor: "#00A7CD" } }}
        >
          <Tab label="Users" sx={tabStyles} />
          <Tab label="Organizations" sx={tabStyles} />
        </Tabs>

        <Box sx={{ padding: "16px 16px 8px" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={tab === 0 ? "Search users..." : "Search organizations..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={listContainerStyles}>
          {tab === 0 ? (
            filteredUsers.length > 0 ? (
              <List dense disablePadding>
                {filteredUsers.map((user) => (
                  <ListItemButton key={user.id} onClick={() => handleSelectUser(user)}>
                    <ListItemText
                      primary={getUserLabel(user)}
                      primaryTypographyProps={{
                        style: { fontFamily: "Avenir Book, sans-serif", fontSize: 14 },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <EmptyState text="No users found." />
            )
          ) : filteredOrgs.length > 0 ? (
            <List dense disablePadding>
              {filteredOrgs.map((org) => (
                <ListItemButton key={org.id} onClick={() => handleSelectOrg(org)}>
                  <ListItemText
                    primary={org.name || org.id}
                    primaryTypographyProps={{
                      style: { fontFamily: "Avenir Book, sans-serif", fontSize: 14 },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <EmptyState text="No organizations found." />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const EmptyState = ({ text }) => (
  <Box sx={{ padding: 3, textAlign: "center" }}>
    <Typography sx={{ color: "#999", fontFamily: "Avenir Book, sans-serif", fontSize: 14 }}>
      {text}
    </Typography>
  </Box>
);

export default CreatedByPickerModal;

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  maxWidth: "90vw",
  bgcolor: "#fff",
  borderRadius: 3,
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const headerStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 16px 8px",
};

const tabStyles = {
  fontFamily: "Avenir Book, sans-serif",
  textTransform: "none",
  fontWeight: 600,
  "&.Mui-selected": { color: "#00A7CD" },
};

const listContainerStyles = {
  maxHeight: 360,
  overflowY: "auto",
  paddingBottom: 1,
};
